import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaUserCircle } from 'react-icons/fa';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import {
  Form,
  FormProvider,
  ValidatedSelect,
} from '@tq/ui-components/dist/components/forms-engine';
import {
  AttentionMessage,
  Box,
  Button,
  Grid,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@tq/ui-components';
import { useIsMobile } from '@tq/ui-components/dist/hooks';

import { useAssignConsultee } from 'api';
import { MobileModal } from 'components';
import { useAssignModal } from 'hooks';
import { useAssignModalConsultees } from './hooks';

type AssignModalProps = {
  isOpen: boolean;
  onClose: (consulteeName?: string) => void;
};

export const AssignModal: React.FC<AssignModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation('assign_modal');
  const isMobile = useIsMobile();
  const { consultationIdsToAssign, consultees } = useAssignModalConsultees();
  const { handleError } = useAssignModal();
  const { mutate: assignConsultee } = useAssignConsultee();

  const handleCancel = () => {
    onClose();
  };

  const handleAssignSelected = (consulteeId: number) => {
    const consulteeName = consultees.find(
      (consultee) => consultee.value === consulteeId
    )?.label;
    assignConsultee(
      {
        consultationIds: consultationIdsToAssign,
        consulteeId,
      },
      { onSuccess: () => onClose(consulteeName), onError: handleError }
    );
  };

  const content = (
    <>
      <ModalHeader aria-label={t('assign_modal', 'Assign modal')}>
        <Grid gridAutoFlow='column' gridGap={4}>
          <FaUserCircle color={COLOURS.blue[600]} />
          <span>{t('assign_header', 'Assign consultation')}</span>
        </Grid>
        <ModalCloseButton />
      </ModalHeader>
      <ModalBody overflow='visible'>
        <FormProvider>
          <Form
            id='assign-consultee-modal-form'
            onValidSubmit={({ consulteeId }) => handleAssignSelected(consulteeId)}
          >
            <ValidatedSelect
              id='consulteeId'
              data-testid='consultee-select'
              label={
                consultationIdsToAssign.length > 1
                  ? t(
                      'select_consultee_label_multiple_work_items',
                      'Select a consultee to assign these work items to'
                    )
                  : t(
                      'select_consultee_label_single_work_item',
                      'Select a consultee to assign this work item to'
                    )
              }
              placeholder={t('choose_consultee_placeholder', 'Choose a consultee')}
              validation={{
                required: {
                  value: true,
                  message: t('consultee_required_error', 'Please select a consultee'),
                },
              }}
            >
              {consultees.map((option) => {
                return (
                  <option key={option.value} value={option.value} disabled={option.disabled}>
                    {option.label}
                  </option>
                );
              })}
            </ValidatedSelect>
          </Form>
        </FormProvider>
        <AttentionMessage
          status='info'
          variant='inline'
          title={
            consultationIdsToAssign.length > 1
              ? t(
                  'assign_notification_message_multiple_work_items',
                  'The assignee will be notified about these work items'
                )
              : t(
                  'assign_notification_message_single_work_item',
                  'The assignee will be notified about this work item'
                )
          }
          mt={8}
        />
      </ModalBody>
    </>
  );

  return isMobile ? (
    <Box data-testid='assign-modal'>
      <MobileModal isOpen={isOpen} onClose={handleCancel} data-testid='assign-modal'>
        {content}
        <ModalFooter>
          <Grid gridGap={4} w='100%'>
            <Button isFullWidth type='submit' form='assign-consultee-modal-form'>
              {t('assign_button_text', 'Assign')}
            </Button>
            <Button isFullWidth variant='tertiary' onClick={handleCancel}>
              {t('cancel_button_text', 'Cancel')}
            </Button>
          </Grid>
        </ModalFooter>
      </MobileModal>
    </Box>
  ) : (
    <Box data-testid='assign-modal'>
      <Modal isOpen={isOpen} onClose={handleCancel}>
        <ModalOverlay />
        <ModalContent>
          {content}
          <ModalFooter justifyContent='end'>
            <Grid gridAutoFlow='column' gridGap={4}>
              <Button variant='tertiary' onClick={handleCancel}>
                {t('cancel_button_text', 'Cancel')}
              </Button>
              <Button type='submit' form='assign-consultee-modal-form'>
                {t('assign_button_text', 'Assign')}
              </Button>
            </Grid>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
